import React, { useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { navLinks, WEB_LINK } from '../../constants';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone } from 'lucide-react';
import { Facebook, Telegram } from '../../icons';
import { CustomLink } from '../link';

const BurgerLatest = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen(!open);
  };

  const menuVariants = {
    closed: {
      x: '-100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 40,
      },
    },
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 40,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      x: -50,
      transition: {
        duration: 0.2,
      },
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const overlayVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  const burgerLineVariants = {
    closed: {
      rotate: 0,
      y: 0,
      opacity: 1,
    },
    open: {
      rotate: 0,
      y: 0,
      opacity: 1,
    },
  };

  return (
    <div className="relative">
      <m.div
        onClick={handleToggle}
        className="cursor-pointer z-50 relative"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="w-6 h-6 flex flex-col justify-center items-center">
          <m.div
            className="w-5 h-0.5 bg-white mb-1"
            variants={burgerLineVariants}
            animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
          <m.div
            className="w-5 h-0.5 bg-white mb-1"
            variants={burgerLineVariants}
            animate={open ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
          <m.div
            className="w-5 h-0.5 bg-white"
            variants={burgerLineVariants}
            animate={open ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </m.div>

      <AnimatePresence>
        {open && (
          <>
            <m.div
              className="fixed inset-0 bg-black w-screen !h-screen bg-opacity-50 z-40"
              variants={overlayVariants}
              initial="closed"
              animate="open"
              exit="closed"
              onClick={handleToggle}
            />

            <m.div
              className="fixed top-0 left-0 !h-screen w-80 bg-white-color z-50 shadow-2xl"
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <m.div
                className="p-5 border-b border-gray-700"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <CustomLink
                  to={'/'}
                  className="flex text-3xl pb-3 font-bold text-black-color"
                >
                  <span>ESM</span>
                  <span className="text-primary">.</span>
                </CustomLink>
              </m.div>

              <nav className="overflow-y-auto h-full relative pb-[50%]">
                <ul className="">
                  {navLinks.map((item, index) => (
                    <m.li
                      key={index}
                      variants={itemVariants}
                    >
                      <m.a
                        aria-label="useful link"
                        href={item.path}
                        className="block text-title-color  text-lg py-4 border-b-2 px-5 transition-colors duration-200"
                        onClick={handleToggle}
                        whileHover={{
                          x: 10,
                          color: '#60a5fa',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        }}
                        whileTap={{ scale: 0.98 }}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 17,
                        }}
                      >
                        {t(item.title)}
                      </m.a>
                    </m.li>
                  ))}
                </ul>
              </nav>

              <m.div
                className="absolute bottom-0 pb-5 w-full bg-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                <div className="border-t border-gray-700 pt-4 px-6">
                  <div className="flex  w-full items-center justify-between">
                    <a
                      aria-label="social link"
                      href={`${WEB_LINK.socials.telegram}`}
                      className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-primary"
                    >
                      <Telegram />
                    </a>
                    <a
                      aria-label="social link"
                      href={`${WEB_LINK.socials.instagram}`}
                      className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-primary"
                    >
                      <Facebook />
                    </a>
                    <a
                      aria-label="map link"
                      href={`mailto:${WEB_LINK.map_url}`}
                      className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-primary"
                    >
                      <MapPin
                        size={18}
                        className="stroke-white-color"
                      />
                    </a>
                    <a
                      aria-label="phone link"
                      href={`tel:${WEB_LINK.phone}`}
                      className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-primary"
                    >
                      <Phone
                        size={18}
                        className="stroke-white-color"
                      />
                    </a>
                  </div>
                </div>
              </m.div>
            </m.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BurgerLatest;
